import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LocalDbService } from '../external/local-db.service';
import { TokenKeysEnum, TokenTypesEnum } from '@shared/enums';

@Injectable({
  providedIn: 'root'
})
export class HttpApiService {
  /**DEPENDENCIES */
  private readonly _http = inject(HttpClient);
  private readonly _localdbService = inject(LocalDbService);
  /**EVENTS */
  public refreshedToken$ = new Subject<string>();

  /**PUBLIC METHODS */
  public get<T>(url: string, extraHeaders: {[key: string]: string} = {}, tokenType = TokenTypesEnum.SERVICE): Observable<T> {
    return this._http.get<T>(`${environment.baseURLService}/v1/${url}`, this.addAuthorizationHeader(extraHeaders, tokenType)).pipe(
      catchError((error) => {
        this.onUnauthorized(error, url);
        return throwError(() => error);
      })
    );
  }

  public post<T>(url: string, data: any, tokenType = TokenTypesEnum.SERVICE): Observable<T> {
    return this._http.post<T>(`${environment.baseURLService}/v1/${url}`, data, this.addAuthorizationHeader({}, tokenType)).pipe(
      catchError((error) => {
        this.onUnauthorized(error, url);
        return throwError(() => error);
      })
    );
  }

  public getBlob(url: string, extraHeaders: {[key: string]: string} = {}, tokenType = TokenTypesEnum.SERVICE): Observable<Blob> {
    return this._http.get(`${environment.baseURLService}/v1/${url}`, {
      ...this.addAuthorizationHeader(extraHeaders, tokenType),
      responseType: 'blob'
    }).pipe(
      catchError((error) => {
        this.onUnauthorized(error, url);
        return throwError(() => error);
      })
    );
  }

  /**METHODS EXTRA */
  public generateToken() {
    return this.post<{ token: string }>('auth/token', {
      apiKey: environment.apiKey,
      clientId: environment.clientId,
      userId: environment.userId,
      password: environment.password
    }).pipe(
      tap((response) => {
        this._localdbService.updateState(TokenKeysEnum.REFRESH_TOKEN_SERVICE, response.token);
        this.refreshedToken$.next(response.token);
      }),
      catchError((error) => {
        console.error('[HttpApiService] Token generation failed:', error);
        return throwError(() => error);
      })
    );
  }

  public downloadFile(url: string, tokenType = TokenTypesEnum.SERVICE): Observable<Blob> {
    return this._http.get(url, {
      responseType: 'blob',
      ...this.addAuthorizationHeader({}, tokenType),
    }).pipe(
      catchError((error) => {
        this.onUnauthorized(error, url);
        return throwError(() => error);
      })
    );
  }

  /**PRIVATE METHODS */
  private addAuthorizationHeader(extraHeaders: {[key: string]: string} = {}, tokenType: TokenTypesEnum) {
    let token: string | null = null;

    switch (tokenType) {
      case TokenTypesEnum.SERVICE:
        token = this._localdbService.accessTokenService;
        break;
      case TokenTypesEnum.APP:
        token = this._localdbService.accessToken;
        break;
    }

    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
          ...extraHeaders
        },
      };
    }

    console.warn('[HttpApiService] No token available for authenticated request to:', tokenType);
    return { headers: { ...extraHeaders } };
  }

  private onUnauthorized(error: any, url: string) {
    if (error.status === 401) {
      console.warn('[HttpApiService] 401 Unauthorized, refreshing token for:', url);
      this.generateToken().subscribe({
        next: (result) => this.refreshedToken$.next(result.token),
        error: (err) => console.error('[HttpApiService] Token refresh failed:', err)
      });
    }
  }
}
